import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Wallet,
  Plus,
  Minus,
  History,
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  CheckCircle,
  AlertTriangle,
  Calculator,
  Coins,
  Edit2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CoinContainerForm = ({ currentValue, onSuccess }) => {
  const [amount, setAmount] = useState(currentValue?.toFixed(2) || "0.00");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (dialogOpen) {
      setAmount(currentValue?.toFixed(2) || "0.00");
    }
  }, [dialogOpen, currentValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) < 0) {
      toast.error("Bitte gültigen Betrag eingeben");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/cash/coin-container`, {
        amount: parseFloat(amount),
        note: note.trim()
      });
      
      toast.success("Münzbehälter aktualisiert");
      setNote("");
      setDialogOpen(false);
      onSuccess();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="edit-coin-container-btn">
          <Edit2 className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Münzbehälter anpassen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
            <div className="flex items-center gap-2 text-amber-800 mb-2">
              <Coins className="w-5 h-5" />
              <span className="font-medium">Aktueller Wert</span>
            </div>
            <div className="text-2xl font-heading font-bold text-amber-600">
              {currentValue?.toFixed(2) || '0.00'}€
            </div>
          </div>

          <div className="space-y-2">
            <Label>Neuer Betrag im Münzbehälter (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="z.B. 50.00"
              data-testid="coin-container-amount-input"
              autoFocus
            />
            <p className="text-xs text-slate-500">
              Geben Sie den aktuellen Gesamtwert des Münzbehälters ein
            </p>
          </div>

          <div className="space-y-2">
            <Label>Notiz (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Nach Zählung"
              data-testid="coin-container-note-input"
            />
          </div>

          <Button 
            type="submit" 
            disabled={saving}
            className="w-full bg-amber-600 hover:bg-amber-700"
            data-testid="coin-container-save-btn"
          >
            {saving ? "Speichern..." : "Münzbehälter aktualisieren"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TransactionForm = ({ onSuccess }) => {
  const [type, setType] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Bitte Betrag eingeben");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/cash/transactions`, {
        amount: parseFloat(amount),
        transaction_type: type,
        note: note.trim()
      });
      
      const actionText = type === "deposit" ? "eingezahlt" : "entnommen";
      toast.success(`${parseFloat(amount).toFixed(2)}€ ${actionText}`);
      setAmount("");
      setNote("");
      setDialogOpen(false);
      onSuccess();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="btn-hover" data-testid="add-transaction-btn">
          <Plus className="w-4 h-4 mr-2" />
          Manuelle Buchung
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Manuelle Buchung</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Art der Buchung</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="transaction-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deposit">Einzahlung (Wechselgeld)</SelectItem>
                <SelectItem value="withdrawal">Entnahme</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Betrag (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="z.B. 50.00"
              data-testid="transaction-amount-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Notiz (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Wechselgeld aufgefüllt"
              data-testid="transaction-note-input"
            />
          </div>

          <Button 
            type="submit" 
            disabled={saving}
            className="w-full bg-rose-600 hover:bg-rose-700"
            data-testid="transaction-save-btn"
          >
            {saving ? "Speichern..." : "Buchen"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ValidationForm = ({ expectedBalance, onSuccess }) => {
  const [amount, setAmount] = useState(expectedBalance?.toFixed(2) || "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (dialogOpen) {
      setAmount(expectedBalance?.toFixed(2) || "");
    }
  }, [dialogOpen, expectedBalance]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) < 0) {
      toast.error("Bitte Betrag eingeben");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/cash/validate`, {
        actual_amount: parseFloat(amount),
        note: note.trim()
      });
      
      const diff = res.data.difference;
      if (Math.abs(diff) < 0.01) {
        toast.success("Kasse stimmt! ✓");
      } else if (diff > 0) {
        toast.success(`Kasse validiert. Überschuss: +${diff.toFixed(2)}€`);
      } else {
        toast.warning(`Kasse validiert. Fehlbetrag: ${diff.toFixed(2)}€`);
      }
      
      setAmount("");
      setNote("");
      setDialogOpen(false);
      onSuccess();
    } catch (err) {
      toast.error("Fehler beim Validieren");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700 btn-hover" data-testid="validate-cash-btn">
          <CheckCircle className="w-4 h-4 mr-2" />
          Kasse validieren
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Kasse validieren</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-center gap-2 text-blue-800 mb-2">
              <Calculator className="w-5 h-5" />
              <span className="font-medium">Erwarteter Umsatz (automatisch berechnet)</span>
            </div>
            <div className="text-3xl font-heading font-bold text-blue-600">
              {expectedBalance?.toFixed(2) || '0.00'}€
            </div>
            <p className="text-sm text-blue-600 mt-1">
              Basierend auf verkauften Produkten seit letzter Validierung
            </p>
          </div>

          <div className="space-y-2">
            <Label>Tatsächlich entnommener Betrag (€)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="z.B. 45.50"
              data-testid="validation-amount-input"
              autoFocus
            />
            <p className="text-xs text-slate-500">
              Wie viel Geld habt ihr tatsächlich aus dem Automaten entnommen?
            </p>
          </div>

          <div className="space-y-2">
            <Label>Notiz (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Münzgeld gezählt"
              data-testid="validation-note-input"
            />
          </div>

          {amount && expectedBalance > 0 && (
            <div className={`rounded-md p-3 ${
              Math.abs(parseFloat(amount) - expectedBalance) < 0.01 
                ? 'bg-emerald-50 border border-emerald-200' 
                : parseFloat(amount) > expectedBalance 
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-center gap-2">
                {Math.abs(parseFloat(amount) - expectedBalance) < 0.01 ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                )}
                <span className="font-medium">
                  Differenz: {(parseFloat(amount) - expectedBalance) >= 0 ? '+' : ''}
                  {(parseFloat(amount) - expectedBalance).toFixed(2)}€
                </span>
              </div>
            </div>
          )}

          <Button 
            type="submit" 
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="validation-save-btn"
          >
            {saving ? "Validieren..." : "Kasse validieren & Betrag buchen"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TransactionItem = ({ transaction }) => {
  const isDeposit = transaction.transaction_type === "deposit";
  const isSale = transaction.transaction_type === "sale";
  const isValidation = transaction.transaction_type === "validation";
  const isWithdrawal = transaction.transaction_type === "withdrawal";
  const isCoinContainer = transaction.transaction_type === "coin_container";
  
  const Icon = isCoinContainer ? Coins : isValidation ? CheckCircle : isSale ? ShoppingCart : (isDeposit ? Plus : Minus);
  const colorClass = isCoinContainer ? 'text-amber-600 bg-amber-100'
    : isValidation ? 'text-emerald-600 bg-emerald-100' 
    : isDeposit || isSale ? 'text-emerald-600 bg-emerald-100' 
    : 'text-rose-600 bg-rose-100';
  
  const getLabel = () => {
    if (isCoinContainer) return "Münzbehälter-Änderung";
    if (isValidation) return "Kassenvalidierung";
    if (isSale) return "Verkauf (automatisch)";
    if (isDeposit) return "Einzahlung";
    return "Entnahme";
  };
  
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${colorClass.split(' ')[1]}`}>
        <Icon className={`w-5 h-5 ${colorClass.split(' ')[0]}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{getLabel()}</div>
        <div className="text-sm text-slate-500 truncate">
          {transaction.note || "-"}
        </div>
      </div>
      <div className="text-right">
        <div className={`font-mono font-bold ${
          isWithdrawal ? 'text-rose-600' : 'text-emerald-600'
        }`}>
          {isWithdrawal ? '-' : '+'}{transaction.amount.toFixed(2)}€
        </div>
        <div className="text-xs text-slate-400">
          {new Date(transaction.created_at).toLocaleDateString('de-DE')}
        </div>
      </div>
    </div>
  );
};

const CashPage = () => {
  const [balance, setBalance] = useState({ current_balance: 0, expected_balance: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balRes, transRes] = await Promise.all([
        axios.get(`${API}/cash/balance`),
        axios.get(`${API}/cash/transactions?limit=100`)
      ]);
      setBalance(balRes.data);
      setTransactions(transRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats from transactions
  const deposits = transactions
    .filter(t => t.transaction_type === "deposit")
    .reduce((sum, t) => sum + t.amount, 0);
  
  const withdrawals = transactions
    .filter(t => t.transaction_type === "withdrawal")
    .reduce((sum, t) => sum + t.amount, 0);
  
  const validations = transactions
    .filter(t => t.transaction_type === "validation")
    .reduce((sum, t) => sum + t.amount, 0);

  // Filter by type
  const [filter, setFilter] = useState("all");
  const filteredTransactions = filter === "all" 
    ? transactions 
    : transactions.filter(t => t.transaction_type === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Kasse
          </h1>
          <p className="text-slate-500 mt-1">
            Kassenbestand und Validierung
          </p>
        </div>
        <div className="flex gap-2">
          <TransactionForm onSuccess={fetchData} />
          <ValidationForm expectedBalance={balance.expected_balance} onSuccess={fetchData} />
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Validated Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Validierter Kassenbestand</p>
                  <p className="text-4xl font-heading font-bold mt-1">
                    {balance.current_balance?.toFixed(2) || '0.00'}€
                  </p>
                  {balance.last_validated_at && (
                    <p className="text-xs text-slate-400 mt-2">
                      Letzte Validierung: {new Date(balance.last_validated_at).toLocaleDateString('de-DE')}
                    </p>
                  )}
                </div>
                <div className="w-16 h-16 bg-white/10 rounded-md flex items-center justify-center">
                  <Wallet className="w-8 h-8" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Expected Balance (to validate) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className={`border-2 ${balance.expected_balance > 0 ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm flex items-center gap-2">
                    <Calculator className="w-4 h-4" />
                    Erwarteter Umsatz (noch nicht validiert)
                  </p>
                  <p className={`text-4xl font-heading font-bold mt-1 ${balance.expected_balance > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                    {balance.expected_balance?.toFixed(2) || '0.00'}€
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Automatisch berechnet aus Verkäufen beim Auffüllen
                  </p>
                </div>
                {balance.expected_balance > 0 && (
                  <div className="w-16 h-16 bg-blue-100 rounded-md flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-blue-600" />
                  </div>
                )}
              </div>
              {balance.expected_balance > 0 && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-sm text-blue-700">
                    💡 Bitte Kasse validieren: Geld entnehmen und tatsächlichen Betrag eintragen
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Münzbehälter Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
      >
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-100 rounded-md flex items-center justify-center">
                  <Coins className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                    Münzbehälter (Wechselgeld)
                  </p>
                  <p className="text-3xl font-heading font-bold text-amber-600 mt-1">
                    {balance.coin_container?.toFixed(2) || '0.00'}€
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Separater Behälter für Wechselgeld im Automaten
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <CoinContainerForm currentValue={balance.coin_container || 0} onSuccess={fetchData} />
                <p className="text-xs text-amber-600">Wert anpassen</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-sm text-amber-700">
                ℹ️ Der Münzbehälter kann Münzen einbehalten und so zu Differenzen bei der erwarteten Verkaufssumme führen.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-md flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Validierte Entnahmen</div>
                  <div className="text-xl font-heading font-bold text-emerald-600">
                    {validations.toFixed(2)}€
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.25 }}
        >
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Einzahlungen</div>
                  <div className="text-xl font-heading font-bold text-blue-600">
                    +{deposits.toFixed(2)}€
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-100 rounded-md flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Manuelle Entnahmen</div>
                  <div className="text-xl font-heading font-bold text-rose-600">
                    -{withdrawals.toFixed(2)}€
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Transactions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.35 }}
      >
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <History className="w-5 h-5" />
                Transaktionen
              </CardTitle>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px]" data-testid="transaction-filter-select">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="validation">Validierungen</SelectItem>
                  <SelectItem value="deposit">Einzahlungen</SelectItem>
                  <SelectItem value="withdrawal">Entnahmen</SelectItem>
                  <SelectItem value="coin_container">Münzbehälter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {filteredTransactions.length > 0 ? (
              <div>
                {filteredTransactions.map((transaction) => (
                  <TransactionItem key={transaction.id} transaction={transaction} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Keine Transaktionen</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default CashPage;
